import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, HStack, useDisclosure } from '@chakra-ui/react';
import { useBlocker, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Button,
    ConfirmationDialog,
    LoadingIndicator,
    Text,
} from '@/components/v4';
import {
    FOOTER_BUTTON_WIDTH,
    PAGE_TITLE,
    PANE_HEIGHT_WITH_BANNER,
    SELECTION_TIP,
    VIEW_MODES,
} from './constants';
import { PlatformEnums } from '@/utils/platforms';
import { NUMBERS_PAGE_PLATFORM_STATE } from '../constants';
import useTeamsBulkNumbers from './hooks/useTeamsBulkNumbers';
import useBulkUpdateRunner from './hooks/useBulkUpdateRunner';
import {
    EMPTY_FILTERS,
    clearFilter,
    fromFilterModel,
    getFilterCounts,
    hasActiveFilters,
    parseNumberSearch,
    toFilterModel,
} from './utils/filters';
import NumbersPane from './components/NumbersPane';
import ViewSwitch from './components/ViewSwitch';
import FilterToolbar from './components/FilterToolbar';
import PasteListModal from './components/PasteListModal';
import BulkUpdateModal from './components/BulkUpdateModal';
import BulkUpdateProgressModal from './components/BulkUpdateProgressModal';
import BulkUpdateStatus from './components/BulkUpdateStatus';
import { getRunSummary } from './utils/bulkUpdate';
import {
    FULL_VIEW_COLUMNS,
    allNumbersRowSelection,
    allNumbersSelectionColumnDef,
    getAllNumbersColumns,
    getAllNumbersRowStyle,
    getRowId,
    getSelectedNumbersColumns,
} from './components/tableConfig';

// Half and half until the screen is wide enough for the toolbar to stay on
// one row in a 5/12 column.
const SPLIT_FLEX = {
    all: { base: '1 1 0', '2xl': '5 1 0' },
    selected: { base: '1 1 0', '2xl': '7 1 0' },
};

const NO_SELECTION_TEMPLATE =
    '<span><b>No Numbers Selected</b><br/>Select numbers from the list on the left to review and bulk update them here.</span>';

const plural = (count, word) => `${count} ${count === 1 ? word : `${word}s`}`;

const PageMessage = ({ status, children }) => (
    <Alert status={status}>
        <AlertIcon />
        <AlertDescription>{children}</AlertDescription>
    </Alert>
);

/**
 * (3222) Find, select and bulk update Microsoft Teams numbers: trunk,
 * Operator Connect settings and Overstamp.
 */
const BulkOperations = () => {
    const {
        rows,
        trunks,
        usages,
        countries,
        tenant,
        isCompanyScope,
        hasTeams,
        hasLoadedPlatforms,
        currentCompany,
        currentScope,
        refetch,
        isLoading,
        isError,
    } = useTeamsBulkNumbers();
    const [view, setView] = useState(VIEW_MODES.SPLIT);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [displayedCount, setDisplayedCount] = useState(0);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [rememberedPaste, setRememberedPaste] = useState('');
    const pasteList = useDisclosure();
    const bulkUpdate = useDisclosure();
    const { run, start, cancel, reset } = useBulkUpdateRunner({
        scope: currentScope,
    });
    const progress = useDisclosure();
    const navigate = useNavigate();
    const isRunning = run.status === 'running';

    // A run lives in this page: refreshing or leaving it cancels the numbers
    // not yet sent, so both ask first.
    useEffect(() => {
        if (!isRunning) return undefined;
        const warnBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warnBeforeUnload);
        return () =>
            window.removeEventListener('beforeunload', warnBeforeUnload);
    }, [isRunning]);
    const leaveBlocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            isRunning && currentLocation.pathname !== nextLocation.pathname,
    );
    // ConfirmationDialog closes after confirming; don't reset a proceeding leave
    const isLeavingRef = useRef(false);

    // Tell the user when a run they hid has finished
    const wasRunningRef = useRef(false);
    useEffect(() => {
        if (isRunning) {
            wasRunningRef.current = true;
            return;
        }
        if (run.status !== 'done' || !wasRunningRef.current) return;
        wasRunningRef.current = false;
        if (progress.isOpen) return;
        const notify =
            run.failed.length || run.notSent ? toast.warning : toast.success;
        const outcome = run.notSent ? 'cancelled' : 'finished';
        notify(`Bulk update ${outcome}: ${getRunSummary(run)}`, {
            onClick: progress.onOpen,
        });
    }, [isRunning, run, progress.isOpen, progress.onOpen]);

    const allGridRef = useRef();
    const selectedGridRef = useRef();

    const selectedRows = useMemo(
        () => rows.filter((row) => selectedIds.has(row.id)),
        [rows, selectedIds],
    );
    const selectedCount = selectedRows.length;

    // Filtering runs on AG Grid's own filter model (filtered-out rows stay in
    // the grid, so ticked numbers stay ticked). The toolbar writes the model
    // and reads it back on every change, so header filters show there too.
    const searchRef = useRef('');
    searchRef.current = filters.search;
    const pasteTotalRef = useRef();

    const applyFilters = useCallback((next) => {
        setFilters((current) =>
            current.search === next.search
                ? current
                : { ...current, search: next.search },
        );
        allGridRef.current?.api?.setFilterModel(toFilterModel(next));
    }, []);

    const handleFilterChanged = useCallback((event) => {
        setFilters(
            fromFilterModel(event.api.getFilterModel(), {
                search: searchRef.current,
                pasteTotal: pasteTotalRef.current,
            }),
        );
    }, []);

    const handleSelectionChanged = useCallback((event) => {
        setSelectedIds(
            new Set(event.api.getSelectedRows().map((row) => row.id)),
        );
    }, []);

    /** Ticks or unticks rows by id; returns how many could be changed. */
    const setRowsSelected = useCallback((ids, isSelected) => {
        const api = allGridRef.current?.api;
        if (!api) return 0;
        const nodes = [...ids]
            .map((id) => api.getRowNode(id))
            .filter((node) => node?.selectable);
        api.setNodesSelected({ nodes, newValue: isSelected });
        return nodes.length;
    }, []);

    const removeFromSelection = useCallback(
        (id) => setRowsSelected([id], false),
        [setRowsSelected],
    );

    // Back to the Numbers page, with the Teams platform still selected
    const goBackToNumbers = () =>
        navigate('/numbers', {
            state: { [NUMBERS_PAGE_PLATFORM_STATE]: PlatformEnums.TEAMS },
        });

    const handlePasteSelect = ({ text, found, matchedIds, total }) => {
        setRememberedPaste(text);
        pasteTotalRef.current = total;
        applyFilters({ ...filters, paste: { digits: found, total } });
        const count = setRowsSelected(matchedIds, true);
        // Greyed out numbers still match the list but can't be ticked
        const skipped = matchedIds.size - count;
        toast.success(
            skipped
                ? `Selected ${count} of ${plural(matchedIds.size, 'matching number')} (${skipped} can't be selected)`
                : `Selected ${plural(count, 'matching number')}`,
        );
        pasteList.onClose();
    };

    const handleApply = (items) => {
        bulkUpdate.onClose();
        progress.onOpen();
        start(items);
    };

    const clearRun = () => {
        progress.onClose();
        reset();
    };

    const handleSelectFailed = () => {
        allGridRef.current?.api?.deselectAll();
        const count = setRowsSelected(
            run.failed.map((failure) => failure.id),
            true,
        );
        clearRun();
        toast.info(`Selected ${plural(count, 'failed number')}`);
    };

    // Switching views only shows or hides columns, so the filters and ticks
    // on All Numbers carry across.
    const changeView = (next) => {
        setView(next);
        const api = allGridRef.current?.api;
        if (!api || next === VIEW_MODES.SELECTED) return;
        api.setColumnsVisible(
            FULL_VIEW_COLUMNS.filter((colId) => api.getColumn(colId)),
            next === VIEW_MODES.FULL,
        );
    };

    const allColumns = useMemo(
        () => getAllNumbersColumns({ countries, tenant }),
        [countries, tenant],
    );
    // Read by the header filter lists through the grid context
    const gridContext = useMemo(
        () => ({ filterCounts: getFilterCounts(rows) }),
        [rows],
    );
    const selectedColumns = useMemo(
        () =>
            getSelectedNumbersColumns({
                view,
                countries,
                tenant,
                onRemove: removeFromSelection,
            }),
        [view, countries, tenant, removeFromSelection],
    );

    if (!currentCompany || !isCompanyScope) {
        return (
            <PageMessage status="warning">
                Select a company to use Numbers Bulk Operations.
            </PageMessage>
        );
    }
    if (!hasLoadedPlatforms) return <LoadingIndicator />;
    if (!hasTeams) {
        return (
            <PageMessage status="info">
                This company does not have the Microsoft Teams platform.
            </PageMessage>
        );
    }
    if (isError) {
        return (
            <PageMessage status="error">
                Microsoft Teams numbers could not be loaded. Please try again
                later.
            </PageMessage>
        );
    }

    // While the run's progress dialog is hidden, a banner above the lists
    // shows it instead, and the lists give up its height.
    const showStatusBanner = !progress.isOpen && run.status !== 'idle';
    const paneHeight = showStatusBanner ? { h: PANE_HEIGHT_WITH_BANNER } : {};

    const cancelButton = (
        <Button
            variant="outline"
            minW={FOOTER_BUTTON_WIDTH}
            onClick={goBackToNumbers}>
            Cancel
        </Button>
    );
    // Clears every filter on All Numbers: search, pasted list, pattern and
    // column filters. Ticked numbers stay ticked.
    const resetFiltersButton = (
        <Button
            variant="outline"
            minW={FOOTER_BUTTON_WIDTH}
            isDisabled={!hasActiveFilters(filters)}
            onClick={() => applyFilters(EMPTY_FILTERS)}>
            Reset Filters
        </Button>
    );
    const bulkUpdateButton = (
        <Button
            minW={FOOTER_BUTTON_WIDTH}
            isDisabled={selectedCount === 0}
            onClick={bulkUpdate.onOpen}>
            Bulk Update ({selectedCount.toLocaleString()})
        </Button>
    );

    // Split View puts Cancel and Reset Filters under All Numbers and Bulk
    // Update under Selected Numbers; Full view shows them all together.
    const allNumbersFooter = (
        <>
            {cancelButton}
            <HStack spacing={3}>
                {resetFiltersButton}
                {view === VIEW_MODES.FULL && bulkUpdateButton}
            </HStack>
        </>
    );
    const selectedNumbersFooter = (
        <>
            {view === VIEW_MODES.SELECTED ? cancelButton : <Box />}
            {bulkUpdateButton}
        </>
    );

    return (
        <Box aria-label="numbers bulk operations">
            <HStack
                justifyContent="space-between"
                marginBottom={5}
                flexWrap="wrap"
                gap={3}>
                <Text sx={{ fontSize: '20', fontWeight: '500' }}>
                    {PAGE_TITLE}
                </Text>
                <ViewSwitch
                    view={view}
                    onChange={changeView}
                    selectedCount={selectedCount}
                />
            </HStack>

            {showStatusBanner && (
                <BulkUpdateStatus
                    run={run}
                    onView={progress.onOpen}
                    onCancel={cancel}
                    onDismiss={clearRun}
                />
            )}

            <Flex gap={4} align="stretch">
                <NumbersPane
                    title="All Numbers"
                    info={SELECTION_TIP}
                    count={`${displayedCount.toLocaleString()} of ${rows.length.toLocaleString()}`}
                    display={view === VIEW_MODES.SELECTED ? 'none' : 'flex'}
                    flex={view === VIEW_MODES.SPLIT ? SPLIT_FLEX.all : '1 1 0'}
                    {...paneHeight}
                    gridRef={allGridRef}
                    toolbar={
                        <FilterToolbar
                            filters={filters}
                            onChange={applyFilters}
                            onRemoveFilter={(key) =>
                                applyFilters(clearFilter(filters, key))
                            }
                            onClearAll={() => applyFilters(EMPTY_FILTERS)}
                            onOpenPasteList={pasteList.onOpen}
                            onRefresh={refetch}
                        />
                    }
                    footer={allNumbersFooter}
                    gridProps={{
                        tableData: { columns: allColumns, data: rows },
                        isLoading,
                        getRowId,
                        context: gridContext,
                        rowSelection: allNumbersRowSelection,
                        selectionColumnDef: allNumbersSelectionColumnDef,
                        getRowStyle: getAllNumbersRowStyle,
                        tooltipShowDelay: 300,
                        onSelectionChanged: handleSelectionChanged,
                        quickFilterText: filters.search,
                        quickFilterParser: parseNumberSearch,
                        onFilterChanged: handleFilterChanged,
                        onModelUpdated: (event) =>
                            setDisplayedCount(event.api.getDisplayedRowCount()),
                        overlayNoRowsTemplate: rows.length
                            ? '<span>No phone numbers match filter</span>'
                            : '<span>No Microsoft Teams numbers found</span>',
                    }}
                />
                <NumbersPane
                    title="Selected Numbers"
                    count={selectedCount.toLocaleString()}
                    display={view === VIEW_MODES.FULL ? 'none' : 'flex'}
                    {...paneHeight}
                    flex={
                        view === VIEW_MODES.SPLIT
                            ? SPLIT_FLEX.selected
                            : '1 1 0'
                    }
                    gridRef={selectedGridRef}
                    footer={selectedNumbersFooter}
                    gridProps={{
                        tableData: {
                            columns: selectedColumns,
                            data: selectedRows,
                        },
                        getRowId,
                        overlayNoRowsTemplate: NO_SELECTION_TEMPLATE,
                    }}
                />
            </Flex>

            <PasteListModal
                isOpen={pasteList.isOpen}
                onClose={pasteList.onClose}
                rows={rows}
                rememberedText={rememberedPaste}
                onSelect={handlePasteSelect}
                isFiltering={!!filters.paste}
                onClearFilter={() => {
                    applyFilters(clearFilter(filters, 'paste'));
                    pasteList.onClose();
                }}
            />
            <BulkUpdateModal
                isOpen={bulkUpdate.isOpen}
                onClose={bulkUpdate.onClose}
                rows={selectedRows}
                trunks={trunks}
                usages={usages}
                tenant={tenant}
                onApply={handleApply}
            />
            <BulkUpdateProgressModal
                run={run}
                isOpen={progress.isOpen}
                onHide={progress.onClose}
                onCancel={cancel}
                onClose={clearRun}
                onSelectFailed={handleSelectFailed}
            />
            <ConfirmationDialog
                isOpen={leaveBlocker.state === 'blocked'}
                title="Leave while numbers are updating?"
                message={`${run.processed.toLocaleString()} of ${run.total.toLocaleString()} numbers are done. Leaving this page cancels the rest; numbers already updated keep their changes.`}
                confirmText="Leave Page"
                cancelText="Stay"
                confirmColorScheme="red"
                onConfirm={() => {
                    isLeavingRef.current = true;
                    leaveBlocker.proceed?.();
                }}
                onClose={() => {
                    if (!isLeavingRef.current) leaveBlocker.reset?.();
                }}
            />
        </Box>
    );
};

export default BulkOperations;
